import { idleTiming } from '@Constants/CommonConstants';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import Heartbeat from '@Services/Heartbeat';
import Auth from '@Services/Auth';
import { setIdleWarningActive, isIdleWarningActive } from '@Services/IdleWarningState';
import store from '@Store';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { typeOfIdleWarningModal } from '@Containers/IdleWarningModal/IdleWarningModalConstants';

const DOCUMENT_NOTIFICATION_TITLE = 'Session expiration';

let userActionInterval = null;
let notification = null;
let browserNotification = null;
let titleNotificationInterval = null;
let defaultTitle = window.document.title;
let warningExpiresAt = null;
let expiryDispatched = false;

// Read by AuthHelper.setLastActionTime to suppress implicit session extensions while the warning is open.
export function isWarningActive() {
  return isIdleWarningActive();
}

export function start() {
  expiryDispatched = false;
  // eslint-disable-next-line no-use-before-define
  userActionInterval = setInterval(checkUserAction, idleTiming.USER_ACTION_CHECK_INTERVAL);
}

export function stop() {
  if (userActionInterval) {
    clearInterval(userActionInterval);
    userActionInterval = null;
  }
}

function checkUserAction() {
  const sessionTimeout = getValueByNs(Auth.getUser(), 'details.sessionTimeout', 0) * 1000;
  if (sessionTimeout === 0) {
    stop();
    return;
  }
  const timeSinceLastAction = Date.now() - Auth.getLastActionTime();

  if (notification) {
    // Warning is latched open. Only an explicit extension — this tab, or another tab that
    // resets the shared lastActionTime — or expiry may close it; never generic activity.
    if (sessionTimeout - timeSinceLastAction > idleTiming.NOTIFICATION_TIMEOUT) {
      // eslint-disable-next-line no-use-before-define
      hideIdleNotifications();
    } else if (warningExpiresAt && Date.now() >= warningExpiresAt) {
      // Failsafe for throttled/background tabs whose modal interval fired late.
      // eslint-disable-next-line no-use-before-define
      processSessionExpiry();
    }
    return;
  }

  if (sessionTimeout - timeSinceLastAction <= idleTiming.NOTIFICATION_TIMEOUT) {
    // Inactive and within N minutes of expiry — open the warning latched to an absolute deadline.
    // eslint-disable-next-line no-use-before-define
    notifyAboutIdle(Number(Auth.getLastActionTime()) + sessionTimeout);
  }
}

// Explicit, repeatable session extension (Continue working / close / Escape / another tab).
export function continueSession() {
  // Clear the warning (and the suppression flag) first so the reset write below is allowed through.
  // eslint-disable-next-line no-use-before-define
  hideIdleNotifications();
  Auth.setLastActionTime();
  expiryDispatched = false;
}

export function processSessionExpiry() {
  if (expiryDispatched) {
    return;
  }
  expiryDispatched = true;
  stop();
  // eslint-disable-next-line no-use-before-define
  hideIdleNotifications();
  Heartbeat.processSessionExpiry();
}

function notifyAboutIdle(expiresAt) {
  warningExpiresAt = expiresAt;
  Catalyst.trackError({
    message: `${DOCUMENT_NOTIFICATION_TITLE} Reaxys recognized no activity and will close the session`,
  });
  store.dispatch(
    updateDialog({
      id: typeOfIdleWarningModal,
      dialogType: typeOfIdleWarningModal,
      movable: false,
      backdrop: false,
      closeButton: false,
      className: 'idle-warning-modal-dialog-wrap',
      expiresAt,
    })
  );
  notification = true;
  setIdleWarningActive(true);

  const isNotificationShownInOtherTabs = AppStorage.instance.get('idleNotification');
  if (window.Notification && Notification.permission === 'granted' && !isNotificationShownInOtherTabs) {
    browserNotification = new Notification('Reaxys', {
      body: `Session will be expired soon`,
    });

    AppStorage.instance.set('idleNotification', true);
  }

  defaultTitle = window.document.title;
  titleNotificationInterval = setInterval(() => {
    window.document.title = window.document.title === DOCUMENT_NOTIFICATION_TITLE ? defaultTitle : DOCUMENT_NOTIFICATION_TITLE;
  }, 1000);
}

function hideIdleNotifications() {
  store.dispatch(
    closeDialog({
      id: typeOfIdleWarningModal,
    })
  );
  notification = null;
  warningExpiresAt = null;
  setIdleWarningActive(false);

  if (browserNotification) {
    browserNotification.close();
    AppStorage.instance.set('idleNotification', false);
  }

  clearInterval(titleNotificationInterval);
  window.document.title = defaultTitle;
}
